import { DataTopic, transformDataFrame, LoadingState } from '@grafana/data';
import { toDataQueryError } from '@grafana/runtime';
import { ReplaySubject, forkJoin, map, catchError, of } from 'rxjs';
import { sceneGraph } from '../core/sceneGraph/index.js';
import { SceneObjectBase } from '../core/SceneObjectBase.js';
import { VariableDependencyConfig } from '../variables/VariableDependencyConfig.js';
import { SceneDataLayerSet } from './SceneDataLayerSet.js';
import { findPanelProfiler } from '../utils/findPanelProfiler.js';

class SceneDataTransformer extends SceneObjectBase {
  constructor(state) {
    super(state);
    this._results = new ReplaySubject(1);
    /**
     * Scan transformations for variable usage and re-process transforms when a variable values change
     */
    this._variableDependency = new VariableDependencyConfig(
      this,
      {
        statePaths: ["transformations"],
        onReferencedVariableValueChanged: () => this.reprocessTransformations()
      }
    );
    this.addActivationHandler(() => this.activationHandler());
  }
  activationHandler() {
    const sourceData = this.getSourceData();
    this._subs.add(sourceData.subscribeToState((state) => this.transform(state.data)));
    if (sourceData.state.data) {
      this.transform(sourceData.state.data);
    }
    return () => {
      if (this._transformSub) {
        this._transformSub.unsubscribe();
      }
    };
  }
  getSourceData() {
    if (this.state.$data) {
      if (this.state.$data instanceof SceneDataLayerSet) {
        throw new Error("SceneDataLayerSet can not be used as data provider for SceneDataTransformer.");
      }
      return this.state.$data;
    }
    if (!this.parent || !this.parent.parent) {
      throw new Error("SceneDataTransformer must either have $data set on it or have a parent.parent with $data");
    }
    return sceneGraph.getData(this.parent.parent);
  }
  setContainerWidth(width) {
    if (this.state.$data && this.state.$data.setContainerWidth) {
      this.state.$data.setContainerWidth(width);
    }
  }
  isDataReadyToDisplay() {
    const dataObject = this.getSourceData();
    if (dataObject.isDataReadyToDisplay) {
      return dataObject.isDataReadyToDisplay();
    }
    return true;
  }
  reprocessTransformations() {
    this.transform(this.getSourceData().state.data, true);
  }
  /**
   * S3.1: Calculate transformation complexity metrics
   */
  _calculateTransformationMetrics(data, transformations) {
    const transformationCount = transformations.length;
    const seriesTransformationCount = transformations.filter((transformation) => {
      if ("options" in transformation || "topic" in transformation) {
        return transformation.topic == null || transformation.topic === DataTopic.Series;
      }
      return true;
    }).length;
    const annotationTransformationCount = transformations.filter((transformation) => {
      if ("options" in transformation || "topic" in transformation) {
        return transformation.topic === DataTopic.Annotations;
      }
      return false;
    }).length;
    return {
      transformationCount,
      seriesTransformationCount,
      annotationTransformationCount
    };
  }
  cancelQuery() {
    var _a, _b;
    (_b = (_a = this.getSourceData()).cancelQuery) == null ? void 0 : _b.call(_a);
  }
  getResultsStream() {
    return this._results;
  }
  clone(withState) {
    const clone = super.clone(withState);
    if (this._prevDataFromSource) {
      clone["_prevDataFromSource"] = this._prevDataFromSource;
    }
    return clone;
  }
  isInViewChanged(isInView) {
    var _a, _b;
    (_b = (_a = this.state.$data) == null ? void 0 : _a.isInViewChanged) == null ? void 0 : _b.call(_a, isInView);
  }
  bypassIsInViewChanged(bypassIsInView) {
    var _a, _b;
    (_b = (_a = this.state.$data) == null ? void 0 : _a.bypassIsInViewChanged) == null ? void 0 : _b.call(_a, bypassIsInView);
  }
  haveAlreadyTransformedData(data) {
    if (!this._prevDataFromSource) {
      return false;
    }
    if (data === this._prevDataFromSource) {
      return true;
    }
    const { series, annotations } = this._prevDataFromSource;
    if (data.series === series && data.annotations === annotations) {
      if (this.state.data && data.state !== this.state.data.state) {
        this.setState({ data: { ...this.state.data, state: data.state } });
      }
      return true;
    }
    return false;
  }
  transform(data, force = false) {
    var _a;
    const timestamp = performance.now();
    const profiler = findPanelProfiler(this);
    const transformStartTime = performance.now();
    let transformationId;
    let endTransformCallback = null;
    if (this.state.transformations.length === 0 || !data) {
      this._prevDataFromSource = data;
      this.setState({ data });
      if (data) {
        this._results.next({ origin: this, data });
      }
      return;
    }
    if (!force && this.haveAlreadyTransformedData(data)) {
      return;
    }
    if (profiler) {
      const transformationTypes = this.state.transformations.map((t) => {
        if ("id" in t) {
          return t.id;
        } else {
          return "customTransformation";
        }
      }).join("+");
      transformationId = transformationTypes || "no-transforms";
      const metrics = this._calculateTransformationMetrics(data, this.state.transformations);
      endTransformCallback = profiler.onDataTransformStart(timestamp, transformationId, metrics);
    }
    const interpolatedTransformations = this._interpolateVariablesInTransformationConfigs(data);
    const seriesTransformations = this._filterAndPrepareTransformationsByTopic(
      interpolatedTransformations,
      (transformation) => {
        if ("options" in transformation || "topic" in transformation) {
          return transformation.topic == null || transformation.topic === DataTopic.Series;
        }
        return true;
      }
    );
    const annotationsTransformations = this._filterAndPrepareTransformationsByTopic(
      interpolatedTransformations,
      (transformation) => {
        if ("options" in transformation || "topic" in transformation) {
          return transformation.topic === DataTopic.Annotations;
        }
        return false;
      }
    );
    if (this._transformSub) {
      this._transformSub.unsubscribe();
    }
    const ctx = {
      interpolate: (value, scopedVars) => {
        var _a2;
        return sceneGraph.interpolate(this, value, { ...(_a2 = data.request) == null ? void 0 : _a2.scopedVars, ...scopedVars });
      }
    };
    const seriesStream = transformDataFrame(seriesTransformations, data.series, ctx);
    const annotationsStream = transformDataFrame(annotationsTransformations, (_a = data.annotations) != null ? _a : []);
    let series = [];
    let annotations = [];
    this._transformSub = forkJoin([seriesStream, annotationsStream]).pipe(
      map((results) => {
        results.forEach((frames) => {
          var _a2;
          for (const frame of frames) {
            if (((_a2 = frame.meta) == null ? void 0 : _a2.dataTopic) === DataTopic.Annotations) {
              annotations.push(frame);
            } else {
              series.push(frame);
            }
          }
        });
        return { ...data, series, annotations };
      }),
      catchError((err) => {
        var _a2;
        const timestamp2 = performance.now();
        const duration = timestamp2 - transformStartTime;
        if (endTransformCallback) {
          endTransformCallback(timestamp2, duration, false, {
            error: err.message || err
          });
        }
        console.error("Error transforming data: ", err);
        const sourceErr = ((_a2 = this.getSourceData().state.data) == null ? void 0 : _a2.errors) || [];
        const transformationError = toDataQueryError(err);
        transformationError.message = `Error transforming data: ${transformationError.message}`;
        const result = {
          ...data,
          state: LoadingState.Error,
          // Combine transformation error with upstream errors
          errors: [...sourceErr, transformationError]
        };
        return of(result);
      })
    ).subscribe((transformedData) => {
      var _a2;
      const timestamp2 = performance.now();
      const duration = timestamp2 - transformStartTime;
      if (endTransformCallback) {
        endTransformCallback(timestamp2, duration, true, {
          outputSeriesCount: transformedData.series.length,
          outputAnnotationsCount: ((_a2 = transformedData.annotations) == null ? void 0 : _a2.length) || 0
        });
      }
      this.setState({ data: transformedData });
      this._results.next({ origin: this, data: transformedData });
      this._prevDataFromSource = data;
    });
  }
  _interpolateVariablesInTransformationConfigs(data) {
    var _a;
    const transformations = this.state.transformations;
    if (this._variableDependency.getNames().size === 0) {
      return transformations;
    }
    const onlyObjects = transformations.every((t) => typeof t === "object");
    if (onlyObjects) {
      return JSON.parse(sceneGraph.interpolate(this, JSON.stringify(transformations), (_a = data.request) == null ? void 0 : _a.scopedVars));
    }
    return transformations.map((t) => {
      var _a2;
      return typeof t === "object" ? JSON.parse(sceneGraph.interpolate(this, JSON.stringify(t), (_a2 = data.request) == null ? void 0 : _a2.scopedVars)) : t;
    });
  }
  _filterAndPrepareTransformationsByTopic(interpolatedTransformations, transformationFilter) {
    return interpolatedTransformations.filter(transformationFilter).map((transformation) => "operator" in transformation ? transformation.operator : transformation);
  }
}

export { SceneDataTransformer };
//# sourceMappingURL=SceneDataTransformer.js.map
